import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Mic, Waves, Loader2, Wand2, Trash2, CheckCircle2, AlertCircle, Coins, SlidersHorizontal, Zap, MessagesSquare, Star, RefreshCw, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import WaveformPlayer from "@/components/WaveformPlayer";
import AudioEffectsPanel from "@/components/AudioEffectsPanel";
import EngineToggle from "@/components/EngineToggle";
import VoiceCloneDialog from "@/components/VoiceCloneDialog";
import VoicePreviewButton from "@/components/VoicePreviewButton";
import DialogueComposer, { EMOTIONS } from "@/components/DialogueComposer";
import DialogueExportPanel from "@/components/DialogueExportPanel";
import CharactersPanel from "@/components/CharactersPanel";
import RuntimeVoicesPanel from "@/components/RuntimeVoicesPanel";
import VoiceFxRow from "@/components/VoiceFxRow";
import api, { audioUrl, formatApiError } from "@/lib/api";
import { uploadBlobChunked } from "@/lib/upload";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const VOICES = ["Rachel", "Aria", "Roger", "Sarah", "Laura", "Charlie", "George", "Callum", "River", "Liam", "Charlotte", "Alice", "Matilda", "Will", "Jessica", "Eric", "Chris", "Brian", "Daniel", "Lily", "Bill"];
const TTS_PRESETS = [
  "Bienvenue, aventurier. Le portail vers les royaumes oubliés vient de s'ouvrir.",
  "Attention ! Ennemis en approche. Préparez vos armes.",
  "Niveau terminé. Score parfait — vous êtes une légende.",
  "Dans un monde où la lumière s'est éteinte, un seul héros peut tout changer.",
];
const SFX_PRESETS = [
  { label: "Tir laser", prompt: "sci-fi laser blaster firing in a spaceship" },
  { label: "Épée dégainée", prompt: "metallic sword being drawn from a scabbard" },
  { label: "Explosion", prompt: "deep cinematic explosion with debris" },
  { label: "Portail magique", prompt: "shimmering magical portal opening, ethereal whoosh" },
  { label: "Pluie & tonnerre", prompt: "heavy rain with distant rolling thunder ambience" },
];

export default function Voice() {
  const { refreshUser, user } = useAuth();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get("project") || null;
  const [projectName, setProjectName] = useState("");
  const [tab, setTab] = useState("tts");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const poll = useRef(null);

  // TTS state
  const [text, setText] = useState("");
  const [voice, setVoice] = useState("Rachel");
  const [stability, setStability] = useState(0.5);
  const [similarity, setSimilarity] = useState(0.75);
  const [style, setStyle] = useState(0.0);
  const [speed, setSpeed] = useState(1.0);

  // SFX state
  const [sfxPrompt, setSfxPrompt] = useState("");
  const [duration, setDuration] = useState(8);
  const [sfxLoop, setSfxLoop] = useState(false);

  // ElevenLabs direct (BYOK)
  const [engine, setEngine] = useState("fal");
  const [elConfigured, setElConfigured] = useState(false);
  const [elVoices, setElVoices] = useState([]);
  const [elVoiceId, setElVoiceId] = useState("");
  const [elLoading, setElLoading] = useState(false);
  const useEleven = engine === "elevenlabs" && elConfigured;

  const fetchElVoices = async (selectId) => {
    setElLoading(true);
    try {
      const { data } = await api.get("/elevenlabs/voices");
      setElVoices(data);
      setElVoiceId((cur) => selectId || (data.some((v) => v.voice_id === cur) ? cur : (data[0]?.voice_id || "")));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Bibliothèque ElevenLabs indisponible.");
    } finally { setElLoading(false); }
  };

  useEffect(() => {
    api.get("/elevenlabs/status").then(({ data }) => setElConfigured(!!data.configured)).catch(() => {});
  }, []);

  useEffect(() => {
    if (useEleven && elVoices.length === 0) fetchElVoices();
  }, [useEleven]);

  const toggleFavorite = async (id) => {
    try {
      const { data } = await api.post(`/elevenlabs/voices/${id}/favorite`);
      toast.success(data.favorite ? "Ajoutée aux favorites ★" : "Retirée des favorites");
      fetchElVoices(id);
    } catch { toast.error("Impossible de mettre à jour le favori."); }
  };

  const deleteElVoice = async (id) => {
    try {
      await api.delete(`/elevenlabs/voices/${id}`);
      toast.success("Voix clonée supprimée.");
      fetchElVoices();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Suppression impossible."); }
  };

  const fetchItems = async () => {
    try {
      const { data } = await api.get("/voice");
      setItems(data);
      return data;
    } catch { return []; }
  };

  useEffect(() => {
    fetchItems();
    return () => clearTimeout(poll.current);
  }, []);

  useEffect(() => {
    if (!projectId) { setProjectName(""); return; }
    api.get("/projects").then(({ data }) => {
      const p = data.find((x) => x.id === projectId);
      setProjectName(p ? p.name : "");
    }).catch(() => {});
  }, [projectId]);

  useEffect(() => {
    const anyProcessing = items.some((i) => i.status === "processing");
    clearTimeout(poll.current);
    if (anyProcessing) {
      poll.current = setTimeout(async () => {
        const data = await fetchItems();
        if (data.some((i) => i.status === "completed" || i.status === "failed")) refreshUser();
      }, 4000);
    }
    return () => clearTimeout(poll.current);
  }, [items]);

  const generateTts = async () => {
    if (!text.trim()) { toast.error("Écrivez le texte à vocaliser."); return; }
    if (useEleven && !elVoiceId) { toast.error("Choisissez une voix ElevenLabs."); return; }
    setLoading(true);
    try {
      const selected = elVoices.find((v) => v.voice_id === elVoiceId);
      await api.post("/voice/tts", {
        text: text.trim(), voice: useEleven ? (selected?.name || "ElevenLabs").slice(0, 40) : voice, stability, similarity_boost: similarity, style, speed, project_id: projectId,
        provider: useEleven ? "elevenlabs" : "fal", voice_id: useEleven ? elVoiceId : null,
      });
      toast.success("Génération de la voix lancée — de ~30 s à quelques minutes.");
      await fetchItems();
      await refreshUser();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const generateSfx = async () => {
    if (!sfxPrompt.trim()) { toast.error("Décrivez l'effet sonore souhaité."); return; }
    setLoading(true);
    try {
      await api.post("/voice/sfx", { prompt: sfxPrompt.trim(), duration, project_id: projectId, provider: useEleven ? "elevenlabs" : "fal", loop: useEleven ? sfxLoop : false });
      toast.success("Génération de l'effet sonore lancée — de ~30 s à quelques minutes.");
      await fetchItems();
      await refreshUser();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/voice/${id}`); await fetchItems(); toast.success("Supprimé."); }
    catch { toast.error("Suppression impossible."); }
  };

  return (
    <div className="p-6 lg:p-8 max-w-6xl overflow-x-hidden">
      <header className="mb-8 flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-4xl font-extrabold flex items-center gap-3">
            <Mic className="text-secondary" /> Voix & SFX
          </h1>
          <p className="text-muted-foreground mt-1">Générez des voix off, des dialogues de jeu et des effets sonores par IA.</p>
        </div>
        <div className="flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 font-mono text-sm" data-testid="voice-credit-balance">
          <Coins size={15} className="text-primary" /> {user?.credits ?? 0} crédits
        </div>
      </header>

      {projectId && (
        <div className="mb-6 rounded-xl border border-primary/30 bg-primary/[0.06] px-4 py-2.5 text-sm flex items-center gap-2" data-testid="voice-project-banner">
          <Waves size={15} className="text-primary" />
          Nouvelles créations rattachées au projet <span className="font-semibold text-primary">{projectName || "sélectionné"}</span>.
        </div>
      )}

      <div className="inline-flex rounded-xl border border-border bg-card p-1 mb-6" data-testid="voice-tabs">
        <TabButton active={tab === "tts"} onClick={() => setTab("tts")} icon={Mic} label="Voix & Narration" testId="tab-tts" />
        <TabButton active={tab === "sfx"} onClick={() => setTab("sfx")} icon={Waves} label="Effets Sonores" testId="tab-sfx" />
        <TabButton active={tab === "dialogue"} onClick={() => setTab("dialogue")} icon={MessagesSquare} label="Dialogue multi-voix" testId="tab-dialogue" />
        <TabButton active={tab === "runtime"} onClick={() => setTab("runtime")} icon={Zap} label="Voix runtime" testId="tab-runtime" />
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        <motion.div key={tab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="lg:col-span-3 min-w-0 rounded-2xl border border-border bg-card p-6 space-y-5">
          <EngineToggle value={engine} onChange={setEngine} configured={elConfigured} />
          {tab === "runtime" ? (
            <RuntimeVoicesPanel onCredits={refreshUser} />
          ) : tab === "dialogue" ? (
            <DialogueComposer useEleven={useEleven} elVoices={elVoices} falVoices={VOICES} projectId={projectId} onCreated={async () => { await fetchItems(); await refreshUser(); }} />
          ) : tab === "tts" ? (
            <>
              <div className="space-y-2">
                <Label>Texte à vocaliser</Label>
                <Textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Ex. Bienvenue, aventurier. Votre quête commence maintenant…"
                  className="min-h-28 bg-background border-border focus-visible:ring-primary resize-none"
                  maxLength={2000}
                  data-testid="tts-text-input"
                />
                <div className="flex flex-wrap gap-2">
                  {TTS_PRESETS.map((p) => (
                    <button key={p} onClick={() => setText(p)} data-testid="tts-preset" className="text-xs px-2.5 py-1 rounded-full border border-secondary/40 text-secondary hover:bg-secondary/15 transition-colors line-clamp-1 max-w-full">
                      {p.slice(0, 42)}…
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                {useEleven ? (
                  <div className="space-y-1.5 sm:col-span-2" data-testid="elevenlabs-voice-panel">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <Label>Voix ElevenLabs {elVoices.length > 0 && <span className="text-muted-foreground font-normal">· {elVoices.length} {elVoices[0]?.fallback ? "voix par défaut" : "dans votre bibliothèque"}</span>}</Label>
                      <VoiceCloneDialog onCloned={(id) => fetchElVoices(id)} />
                    </div>
                    <div className="flex items-center gap-2">
                      <Select value={elVoiceId} onValueChange={setElVoiceId} disabled={elLoading || elVoices.length === 0}>
                        <SelectTrigger className="bg-background border-border flex-1" data-testid="elevenlabs-voice-select">
                          <SelectValue placeholder={elLoading ? "Chargement…" : "Aucune voix disponible"} />
                        </SelectTrigger>
                        <SelectContent className="glass border-border max-h-72">
                          {elVoices.map((v) => (
                            <SelectItem key={v.voice_id} value={v.voice_id}>
                              {v.favorite ? "★ " : ""}{v.name}{v.cloned_by_me ? " · clonée" : v.category === "cloned" ? " · clone" : v.category === "premade" ? "" : v.category ? ` · ${v.category}` : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <VoicePreviewButton voiceId={elVoiceId} />
                      <Button type="button" variant="outline" size="icon" onClick={() => toggleFavorite(elVoiceId)} disabled={!elVoiceId} title="Voix favorite (apparaît en premier)"
                        className={`shrink-0 ${elVoices.find((v) => v.voice_id === elVoiceId)?.favorite ? "border-amber-400/60 text-amber-400" : "border-border text-muted-foreground hover:text-amber-400"}`} data-testid="elevenlabs-voice-favorite">
                        <Star size={15} fill={elVoices.find((v) => v.voice_id === elVoiceId)?.favorite ? "currentColor" : "none"} />
                      </Button>
                      {elVoices.find((v) => v.voice_id === elVoiceId)?.cloned_by_me && (
                        <Button variant="ghost" size="icon" onClick={() => deleteElVoice(elVoiceId)} className="text-muted-foreground hover:text-destructive shrink-0" data-testid="elevenlabs-voice-delete" title="Supprimer cette voix clonée">
                          <Trash2 size={15} />
                        </Button>
                      )}
                    </div>
                    {elVoices[0]?.fallback && (
                      <p className="text-[11px] text-amber-400/90" data-testid="elevenlabs-voices-fallback">Votre clé n'a pas la permission « Voices » : la bibliothèque du compte et le clonage sont indisponibles — les voix par défaut ElevenLabs restent utilisables.</p>
                    )}
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    <Label>Voix</Label>
                    <Select value={voice} onValueChange={setVoice}>
                      <SelectTrigger className="bg-background border-border" data-testid="tts-voice-select"><SelectValue /></SelectTrigger>
                      <SelectContent className="glass border-border max-h-64">
                        {VOICES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <SliderField label="Vitesse" value={speed} min={0.7} max={1.2} step={0.05} onChange={setSpeed} fmt={(v) => `${v.toFixed(2)}×`} testId="tts-speed" />
                <SliderField label="Stabilité" value={stability} min={0} max={1} step={0.05} onChange={setStability} fmt={(v) => `${Math.round(v * 100)}%`} testId="tts-stability" />
                <SliderField label="Similarité" value={similarity} min={0} max={1} step={0.05} onChange={setSimilarity} fmt={(v) => `${Math.round(v * 100)}%`} testId="tts-similarity" />
                <SliderField label="Style" value={style} min={0} max={1} step={0.05} onChange={setStyle} fmt={(v) => `${Math.round(v * 100)}%`} testId="tts-style" />
              </div>

              <Button onClick={generateTts} disabled={loading} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 glow-primary gap-2" data-testid="tts-generate">
                {loading ? <Loader2 className="animate-spin" size={16} /> : <Wand2 size={16} />} Générer la voix · 2 cr
              </Button>
            </>
          ) : (
            <>
              <div className="space-y-2">
                <Label>Décrivez l'effet sonore</Label>
                <Input
                  value={sfxPrompt}
                  onChange={(e) => setSfxPrompt(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && generateSfx()}
                  placeholder="Ex. tir laser dans un vaisseau spatial"
                  className="bg-background border-border focus-visible:ring-primary"
                  maxLength={500}
                  data-testid="sfx-prompt-input"
                />
                <div className="flex flex-wrap gap-2">
                  {SFX_PRESETS.map((p) => (
                    <button key={p.label} onClick={() => setSfxPrompt(p.prompt)} data-testid="sfx-preset" className="text-xs px-2.5 py-1 rounded-full border border-secondary/40 text-secondary hover:bg-secondary/15 transition-colors">
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              <SliderField label="Durée" value={duration} min={1} max={30} step={1} onChange={(v) => setDuration(Math.round(v))} fmt={(v) => `${Math.round(v)}s`} testId="sfx-duration" />
              {useEleven && (
                <div className="flex items-center justify-between rounded-xl border border-border bg-background px-4 py-3" data-testid="sfx-loop-row">
                  <div>
                    <div className="text-sm font-medium">Boucle parfaite</div>
                    <div className="text-xs text-muted-foreground">ElevenLabs génère un SFX bouclable sans coupure (ambiances)</div>
                  </div>
                  <Switch checked={sfxLoop} onCheckedChange={setSfxLoop} data-testid="sfx-loop-switch" />
                </div>
              )}

              <Button onClick={generateSfx} disabled={loading} className="w-full bg-secondary text-white hover:bg-secondary/90 gap-2" data-testid="sfx-generate">
                {loading ? <Loader2 className="animate-spin" size={16} /> : <Wand2 size={16} />} Générer l'effet · 3 cr
              </Button>
            </>
          )}
        </motion.div>

        <div className="lg:col-span-2 space-y-4">
        {tab === "dialogue" && <CharactersPanel refreshKey={items.length} />}
        <div className="rounded-2xl border border-border bg-card p-6 font-mono text-xs">
          <div className="text-muted-foreground mb-2">// Astuce</div>
          <p className="text-sm text-foreground/80 leading-relaxed font-sans">
            {tab === "dialogue"
              ? "Écrivez votre scène au format « Personnage: réplique ». Chaque personnage reçoit sa propre voix ; toutes les répliques sont générées d'un coup (2 cr par réplique) et numérotées dans vos créations."
              : useEleven
              ? "Mode ElevenLabs direct : vos voix de compte et vos clones sont disponibles. Le clonage instantané fonctionne avec 1 à 3 minutes d'audio propre, sans musique de fond."
              : tab === "tts"
              ? "La voix multilingue gère parfaitement le français. Baissez la stabilité pour plus d'expressivité, montez-la pour une narration régulière."
              : "Décrivez précisément le son (matière, action, ambiance). Les effets courts (2-8s) sont parfaits pour les événements de jeu ; les plus longs pour les ambiances."}
          </p>
        </div>
        </div>
      </div>

      <div className="mt-10">
        <h2 className="font-display text-lg font-bold mb-4">Vos créations</h2>
        {items.length === 0 ? (
          <div className="text-sm text-muted-foreground/70 rounded-2xl border border-dashed border-border p-8 text-center" data-testid="voice-empty">
            Aucune création pour l'instant — générez votre première voix ou effet sonore.
          </div>
        ) : (
          <div className="space-y-3" data-testid="voice-list">
            {items.map((v) => (
              <VoiceItem key={v.id} v={v} onDelete={remove} onSaved={fetchItems} elVoices={elVoices} onRegenerated={async () => { await fetchItems(); await refreshUser(); }} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function VoiceItem({ v, onDelete, onSaved, elVoices = [], onRegenerated }) {
  const isTts = v.kind === "tts";
  const [fxOpen, setFxOpen] = useState(false);
  const [regenOpen, setRegenOpen] = useState(false);
  const [regenVoice, setRegenVoice] = useState("");
  const [regenEmotion, setRegenEmotion] = useState(v.emotion || "neutre");
  const [exportOpen, setExportOpen] = useState(false);
  const [regenLoading, setRegenLoading] = useState(false);
  const isEleven = v.provider === "elevenlabs";
  const regenOptions = isEleven ? elVoices.map((x) => ({ id: x.voice_id, label: `${x.favorite ? "★ " : ""}${x.name.split(" - ")[0]}` })) : VOICES.map((x) => ({ id: x, label: x }));

  const regenerate = async () => {
    if (!regenVoice) { toast.error("Choisissez une voix."); return; }
    setRegenLoading(true);
    try {
      const label = regenOptions.find((o) => o.id === regenVoice)?.label.replace("★ ", "") || regenVoice;
      await api.post(`/voice/${v.id}/regenerate`, isEleven ? { voice_id: regenVoice, voice: label, emotion: regenEmotion } : { voice: regenVoice, emotion: regenEmotion });
      toast.success(`Réplique ${v.line_index} relancée avec ${label} (2 cr).`);
      setRegenOpen(false);
      onRegenerated?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setRegenLoading(false); }
  };
  const title = v.title || (isTts ? (v.text || "").slice(0, 60) : (v.prompt || ""));

  const saveFx = async (blob, settings, name) => {
    const tid = toast.loading("Envoi de la version traitée…");
    try {
      const uploadId = await uploadBlobChunked(blob, (i, n) => toast.loading(`Envoi de la version traitée… ${i}/${n}`, { id: tid }));
      await api.post(`/voice/${v.id}/fx-save`, { upload_id: uploadId, name: name || null, settings });
      toast.success("Version traitée enregistrée dans vos créations ✨", { id: tid });
      setFxOpen(false);
      onSaved?.();
    } catch {
      toast.error("Enregistrement impossible. Réessayez.", { id: tid });
    }
  };

  return (
    <motion.div layout initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl border border-border bg-card p-4" data-testid={`voice-item-${v.seq}`}>
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <Badge className={isTts ? "bg-primary/15 text-primary border-primary/30 gap-1" : "bg-secondary/15 text-secondary border-secondary/30 gap-1"}>
              {isTts ? <Mic size={11} /> : <Waves size={11} />} {isTts ? `Voix · ${v.voice}` : `SFX · ${v.duration}s`}
            </Badge>
            {v.fx_of && <Badge className="bg-secondary/15 text-secondary border-secondary/30 gap-1" data-testid={`voice-fx-badge-${v.seq}`}><SlidersHorizontal size={11} /> FX</Badge>}
            {v.dialogue_id && <Badge className="bg-primary/10 text-primary border-primary/30 gap-1" data-testid={`voice-dialogue-badge-${v.seq}`}><MessagesSquare size={11} /> {v.dialogue_title} · {v.line_index}/{v.line_total} · {v.character}{v.emotion && v.emotion !== "neutre" ? ` · ${v.emotion}` : ""}</Badge>}
            {v.provider === "elevenlabs" && <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/30 gap-1" data-testid={`voice-eleven-badge-${v.seq}`}><Zap size={11} /> ElevenLabs</Badge>}
            {v.status === "processing" && <Badge className="bg-accent/20 text-accent border-accent/30 gap-1"><Loader2 size={11} className="animate-spin" /> En cours</Badge>}
            {v.status === "completed" && <Badge className="bg-primary/15 text-primary border-primary/30 gap-1"><CheckCircle2 size={11} /> Prêt</Badge>}
            {v.status === "failed" && <Badge className="bg-destructive/20 text-destructive border-destructive/30 gap-1"><AlertCircle size={11} /> Échec</Badge>}
          </div>
          <p className="text-sm text-muted-foreground line-clamp-1">{title}</p>
        </div>
        <Button variant="ghost" size="icon" onClick={() => onDelete(v.id)} data-testid={`voice-delete-${v.seq}`} className="text-muted-foreground hover:text-destructive shrink-0">
          <Trash2 size={16} />
        </Button>
      </div>
      {v.status === "processing" && (
        <div className="flex items-center gap-3 text-muted-foreground text-sm py-4 justify-center">
          <Loader2 className="animate-spin text-primary" size={16} /> Génération en cours…
        </div>
      )}
      {v.status === "failed" && (
        <div className="text-sm text-destructive/90 py-2 px-3 rounded-lg bg-destructive/10 border border-destructive/20">
          {v.error || "La génération a échoué."} Vos crédits ont été remboursés.
        </div>
      )}
      {v.status === "completed" && v.audio_path && (
        <WaveformPlayer
          src={audioUrl(v.audio_path)}
          label={isTts ? `Voix — ${v.audio_ext?.toUpperCase()}` : `SFX — ${v.audio_ext?.toUpperCase()}`}
          downloadName={`${v.kind}-${v.seq}.${v.audio_ext}`}
          testId={`voice-player-${v.seq}`}
        />
      )}
      {v.status === "completed" && v.audio_path && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {v.dialogue_id && (
            <>
              <Button variant="outline" size="sm" onClick={() => setRegenOpen((o) => !o)} className="border-primary/40 text-primary hover:bg-primary/10 gap-2" data-testid={`voice-regen-open-${v.seq}`}>
                <RefreshCw size={14} /> Régénérer la réplique
              </Button>
              {v.line_index === 1 && (
                <Button variant="outline" size="sm" onClick={() => setExportOpen((o) => !o)} className="border-secondary/40 text-secondary hover:bg-secondary/10 gap-2" data-testid={`dialogue-export-${v.seq}`}>
                  <Download size={14} /> Exporter le dialogue
                </Button>
              )}
            </>
          )}
          <VoiceFxRow v={v} onSaved={onSaved} />
          <Dialog open={fxOpen} onOpenChange={setFxOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="border-secondary/40 text-secondary hover:bg-secondary/10 gap-2" data-testid={`voice-fx-open-${v.seq}`}>
                <SlidersHorizontal size={14} /> Effets
              </Button>
            </DialogTrigger>
            <DialogContent className="glass border-border max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-display flex items-center gap-2">
                  <SlidersHorizontal className="text-secondary" size={20} /> Rack d'effets — {isTts ? "Voix" : "SFX"} #{v.seq}
                </DialogTitle>
                <DialogDescription>Reverb, écho, EQ, vitesse et fondus — traitement local, export WAV ou nouvelle création.</DialogDescription>
              </DialogHeader>
              <AudioEffectsPanel src={audioUrl(v.audio_path)} downloadName={`${v.kind}-${v.seq}-fx.wav`} onSave={saveFx} defaultName={`${isTts ? "Voix" : "SFX"} #${v.seq} (FX)`} />
            </DialogContent>
          </Dialog>
        </div>
      )}
      {exportOpen && v.dialogue_id && <DialogueExportPanel dialogueId={v.dialogue_id} seq={v.seq} projectId={v.project_id} onProjectChanged={onSaved} onTranslated={onRegenerated} />}
      {regenOpen && (
        <div className="mt-2 flex flex-wrap items-center gap-2 rounded-xl border border-primary/25 bg-primary/[0.04] p-3" data-testid={`voice-regen-panel-${v.seq}`}>
          <span className="text-xs text-muted-foreground">Nouvelle voix pour <span className="text-foreground font-semibold">{v.character}</span> :</span>
          <Select value={regenVoice} onValueChange={setRegenVoice}>
            <SelectTrigger className="bg-background border-border h-8 text-xs w-48" data-testid={`voice-regen-select-${v.seq}`}><SelectValue placeholder="Choisir une voix" /></SelectTrigger>
            <SelectContent className="glass border-border max-h-64">
              {regenOptions.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={regenEmotion} onValueChange={setRegenEmotion}>
            <SelectTrigger className="bg-background border-border h-8 text-xs w-32" data-testid={`voice-regen-emotion-${v.seq}`}><SelectValue /></SelectTrigger>
            <SelectContent className="glass border-border">
              {EMOTIONS.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" onClick={regenerate} disabled={regenLoading} className="h-8 bg-primary text-primary-foreground hover:bg-primary/90 gap-1.5" data-testid={`voice-regen-submit-${v.seq}`}>
            {regenLoading ? <Loader2 className="animate-spin" size={13} /> : <RefreshCw size={13} />} Régénérer · 2 cr
          </Button>
        </div>
      )}
    </motion.div>
  );
}

const TabButton = ({ active, onClick, icon: Icon, label, testId }) => (
  <button onClick={onClick} data-testid={testId}
    className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${active ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
    <Icon size={15} /> {label}
  </button>
);

const SliderField = ({ label, value, min, max, step, onChange, fmt, testId }) => (
  <div className="space-y-1.5">
    <div className="flex items-center justify-between">
      <Label>{label}</Label>
      <span className="font-mono text-xs text-primary">{fmt(value)}</span>
    </div>
    <Slider value={[value]} min={min} max={max} step={step} onValueChange={(x) => onChange(x[0])} data-testid={testId} />
  </div>
);
